import React, { useState, useRef, useEffect, ReactElement } from "react";
import styled from "styled-components";
import {
	Content,
	ContentItem,
	ContentBox,
	Frame,
	ImgFrame,
	Number,
	NumberBar,
	NumberIndicator,
	Text,
	Heading,
	Caption,
	Img,
} from "./styles";
import Nav from "./Nav";
import { useAnimation } from "framer-motion";
import { usePrevious } from "@src/utils/usePrevious";
import { GatsbyImageProps } from "gatsby-plugin-image";

interface Props {
	info: {
		heading: string;
		caption: string;
		img:
			| React.ReactElement<HTMLImageElement>
			| React.FunctionComponent<GatsbyImageProps>;
	}[];
	windowW: number | null;
	className?: string;
}

const Intro: React.FC<Props> = (props) => {
	const { info, windowW, className } = props;
	const [activeItem, setActiveItem] = useState(0);
	const previousItem = usePrevious(activeItem);
	const [numberItemPos, setNumberItemPos] = useState(0);
	const [contentsize, setContentSize] = useState({ width: 0, height: 0 });
	const numberBarRef = useRef<HTMLDivElement>(null);
	const numberRef = new Array(info.length)
		.fill(null)
		.map(() => useRef<HTMLButtonElement>(null));
	const contentRef = useRef<HTMLDivElement>(null);
	const increment = () =>
		setActiveItem(activeItem == info.length - 1 ? 0 : activeItem + 1);
	const decrement = () =>
		setActiveItem(activeItem == 0 ? info.length - 1 : activeItem - 1);
	const contentItemControl = useAnimation();
	useEffect(() => {
		const getNumberPos = () => {
			const numberCurrent = numberRef[activeItem]?.current;
			if (numberCurrent && numberBarRef.current) {
				const numberPos = numberCurrent.getBoundingClientRect();
				const barPos = numberBarRef.current.getBoundingClientRect();
				setNumberItemPos(numberPos.x - barPos.x + numberPos.width / 2);
			}
		};
		const updateItem = async () => {
			contentItemControl.set((index) => {
				if (index == activeItem) {
					return {
						opacity: 1,
						zIndex: 0,
					};
				} else if (index == previousItem) {
					return {
						opacity: 1,
						zIndex: 1,
					};
				} else return { opacity: 0 };
			});
			contentItemControl.start((index) => {
				if (index == activeItem) {
					return {
						opacity: 1,
					};
				} else return { opacity: 0 };
			});
		};
		getNumberPos();
		updateItem();
	}, [activeItem]);
	useEffect(() => {
		const getContentSize = () => {
			if (contentRef.current) {
				const contentInfo = contentRef.current.getBoundingClientRect();
				setContentSize({
					width: contentInfo.width,
					height: contentInfo.height,
				});
			}
		};
		getContentSize();
	}, [windowW]);

	return (
		<Frame className={className}>
			<ContentBox>
				<Nav to="prev" onClick={decrement} />
				<Content ref={contentRef} windowW={windowW}>
					{info.map((val, index) => (
						<ContentItem
							contentsize={contentsize}
							custom={index}
							animate={contentItemControl}
							key={index}
						>
							<ImgFrame>{val.img}</ImgFrame>
							<Text>
								<Heading>{val.heading}</Heading>
								<Caption>{val.caption}</Caption>
							</Text>
						</ContentItem>
					))}
				</Content>
				<Nav to="next" onClick={increment} />
			</ContentBox>
			<NumberBar ref={numberBarRef}>
				{new Array(info.length).fill(null).map((val, index) => (
					<Number
						active={index == activeItem}
						ref={numberRef[index]}
						onClick={() => setActiveItem(index)}
						key={`number${index}`}
					>
						{index + 1}
					</Number>
				))}
				<NumberIndicator
					animate={{
						left: numberItemPos,
						transition: { type: "spring", stiffness: 300, damping: 50 },
					}}
				/>
			</NumberBar>
		</Frame>
	);
};

export default styled(Intro)``;
